/**
 * GPS Utilities
 * Provides functions for GPS calculations, location tracking, and distance formatting
 */

const EARTH_RADIUS_METERS = 6371000;

/**
 * Convert degrees to radians
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 * @param {number} radians - Angle in radians
 * @returns {number} Angle in degrees
 */
function toDegrees(radians) {
  return radians * (180 / Math.PI);
}

/**
 * Calculate distance between two geographic points using Haversine formula
 * @param {Object} from - Starting point with {lat, lng}
 * @param {Object} to - Ending point with {lat, lng}
 * @returns {number} Distance in meters
 */
export function calculateDistance(from, to) {
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate bearing (direction) from one point to another
 * @param {Object} from - Starting point with {lat, lng}
 * @param {Object} to - Ending point with {lat, lng}
 * @returns {number} Bearing in degrees (0-360, where 0=north, 90=east, 180=south, 270=west)
 */
export function calculateBearing(from, to) {
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const y = Math.sin(deltaLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

  let bearing = toDegrees(Math.atan2(y, x));

  // Normalize to 0-360 range
  bearing = (bearing + 360) % 360;

  return bearing;
}

/**
 * Format distance in human-readable format
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance string (e.g., "150m" or "2.5km")
 */
export function formatDistance(meters) {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Request user's current GPS location
 * @returns {Promise<Object|null>} Location object {lat, lng, accuracy} or null on error
 */
export async function getCurrentLocation() {
  return new Promise((resolve) => {
    if (!navigator?.geolocation) {
      console.error('Geolocation is not supported by this browser');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        console.error('Error getting current location:', error.message);
        resolve(null);
      },
      { enableHighAccuracy: true }
    );
  });
}

/**
 * Watch user's location continuously
 * @param {Function} callback - Function to call with location updates {lat, lng, accuracy}
 * @returns {number} Watch ID for later clearing
 */
export function watchLocation(callback) {
  if (!navigator?.geolocation) {
    console.error('Geolocation is not supported by this browser');
    return null;
  }

  return navigator.geolocation.watchPosition(
    (position) => {
      callback({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });
    },
    (error) => {
      console.error('Error watching location:', error.message);
    },
    { enableHighAccuracy: true }
  );
}

/**
 * Stop watching location
 * @param {number} watchId - Watch ID returned from watchLocation()
 */
export function stopWatchingLocation(watchId) {
  if (!navigator?.geolocation) {
    console.error('Geolocation is not supported by this browser');
    return;
  }

  navigator.geolocation.clearWatch(watchId);
}

let broadcastWatchId = null;

/**
 * Start a single shared position watch that broadcasts every fix as a
 * 'gpsupdate' window event with detail {lat, lng, accuracy}.
 *
 * Per CLAUDE.md, sensor data flows through window events rather than React
 * callbacks — a React callback registered in a mount effect captures its props
 * forever, which is exactly the stale-target bug this replaces.
 *
 * Idempotent: calling it again while a watch is running is a no-op.
 */
export function startLocationBroadcast() {
  if (broadcastWatchId !== null) return;

  broadcastWatchId = watchLocation((location) => {
    window.dispatchEvent(new CustomEvent('gpsupdate', { detail: location }));
  });
}

/**
 * Stop the shared position watch started by startLocationBroadcast().
 */
export function stopLocationBroadcast() {
  if (broadcastWatchId === null) return;

  stopWatchingLocation(broadcastWatchId);
  broadcastWatchId = null;
}
